import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslate } from "react-admin";
import FeatureCodes from "@wallet-manager/node-types/dist/src/featureCode/FeaturesWalletManagement";

import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";
import WalletRiskLevelRescanStatus from "@wallet-manager/node-types/dist/types/postgres/const/WalletRiskLevelRescanStatus";

import { usePermission } from "../../../hooks";

import {
  Box,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from "../../../components/MuiGenerals";
import TableTab from "../../../components/TableTab";
import RiskCheckingRecordTabList, {
  getChainNameWithETHSeries,
} from "../../../components/WalletManagement/RiskCheckingRecordTabList";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useChains } from "../../../components/FetchConfig";
import { useDatePicker } from "../../../components/DatePicker";
import GeneralBtn from "../../../components/GeneralBtn";
import MpTextField from "../../../components/MpTextField";
import {
  useExportRiskCheckingRecords,
  useGetRiskCheckingRecords,
} from "../../../components/WalletManagement/hooks/useRescanRiskLevel";
import {
  downloadFiles,
  findChainInfo,
  getFullApiResponse,
  separateBatchStrings,
  toDisplayTime,
} from "../../../utils/helper";
import { customSx } from "../../../utils/styling";
import { DivideLine, genField, useRefresh } from "../../../utils/HelperComp";
import { listFace } from "../../Tools/AuditLogTabView";
import { useZusDialogStore } from "../../../zustand/store";
import { tableConfig } from "src/utils/constant";

// Coerce `null` → `undefined`. axios serializes `null` into the query string
// as the literal "null" (e.g. `?chainId=null`), but skips `undefined`
// entirely. Call sites use this to keep empty filter values out of the URL
// even when the underlying field is `null` rather than `undefined`.
const nonNullish = <T,>(value: T | null | undefined): T | undefined =>
  value === null || value === undefined ? undefined : value;

// EPtQuery

interface fieldsFace {
  displayNamesString: string;
  wallet_address: string;
  chain_name: string;
  outcome: string;
  triggered_by: string;
}

const initFields: fieldsFace = {
  displayNamesString: "",
  wallet_address: "",
  chain_name: "",
  outcome: "",
  triggered_by: "",
};

const Pkey = FeatureCodes.WalletManagement.TabRiskCheckingRecord;

const RiskCheckingRecordTab = () => {
  const [list, setList] = useState<listFace>({ rows: [], count: 0 });
  const [merkleSciApiTimeoutSec, setMerkleSciApiTimeoutSec] = useState<
    string | undefined
  >(undefined);
  const [page, setPage] = useState(0);
  const [refreshBool, refresh] = useRefresh();
  const { getRiskCheckingRecords } = useGetRiskCheckingRecords();
  const { exportRiskCheckingRecords } = useExportRiskCheckingRecords();
  const zusDialog = useZusDialogStore();
  // `chainObj` is keyed by chain name → returns the chain's `chain_type` and
  // `chain_id`, which the API needs as filter params. The dropdown's selected
  // value (`fields.chain_name`) is the key into this map.
  const { chainObj } = useChains();
  const { hasPermission } = usePermission();
  const translate = useTranslate();

  // Export the currently-displayed filter set as CSV. The export uses the
  // same columns the user sees on screen (translated where applicable),
  // plus a Details column carrying the raw `merkle_science_response` for
  // anyone who wants the full payload (the table column renders the
  // Provider Response dialog, but the CSV just dumps the JSON).
  const onExport = async () => {
    // Build `tEnum` fresh on every click. The closure over `translate`
    // captures the i18n provider's *current* locale at call time, so when the
    // user switches language and then exports, the CSV reflects the new
    // locale — not whatever locale was active when the component first
    // mounted. (Capturing at module level or hook-render time would let the
    // closure grow stale; defining it inside the handler keeps it fresh.)
    const tEnum = (key: string) => translate(`enumConstants.${key}`);

    // Same reasoning for riskLevelText — bound at click time so it
    // translates against the locale the user is currently looking at.
    const riskLevelText = (value: unknown): string => {
      if (value === null || value === undefined || value === "") return "";
      const numeric = Number(value);
      const enumName = EnumRiskLevel[numeric];
      return enumName !== undefined ? tEnum(enumName) : String(value);
    };

    if (list.count === 0) {
      return translate("component.no_data_export");
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      exportRiskCheckingRecords({ ...cacheParams, page, pageSize }, { signal });
    const rawRes = await getFullApiResponse(apiFn, list.count, true);
    if (rawRes.length === 0) return;
    // Map each row into the same column set the table renders. Column
    // headers use the project's translation keys so the CSV picks up
    // the user's current locale (English / 简中) automatically. Translated
    // enum values come from the project's `enumConstants` namespace. The
    // chain name is resolved from `(chain_type, chain_id)` via
    // `findChainInfo` so the CSV matches what the user sees on screen.
    const res = rawRes.map((row: any) => {
      const chainName = getChainNameWithETHSeries(
        row?.wallet?.chain_type,
        row?.wallet?.chain_id,
        row?.wallet?.wallet_type,
      );
      const numericStatus = Number(row?.status);
      const enumStatus = WalletRiskLevelRescanStatus[numericStatus];
      const statusText = enumStatus ? tEnum(`RescanStatus_${enumStatus}`) : "";
      return {
        [translate("walletManagement.rescan_time")]:
          // Match the table column — ISO from the backend goes through
          // `toDisplayTime` so the CSV reads "2026-09-28 03:02:27" rather than
          // the raw "2026-09-28T03:02:27.669Z" timestamp.
          row?.created_date ? toDisplayTime(row.created_date) : "",
        [translate("walletManagement.walletAddress")]: row?.wallet_address,
        [translate("walletManagement.chain_name")]: chainName,
        [translate("walletManagement.display_name")]:
          row?.wallet?.display_name ?? row?.wallet_address ?? "",
        [translate("walletManagement.before")]: riskLevelText(
          row?.current_risk_level,
        ),
        [translate("walletManagement.returned")]: riskLevelText(
          row?.response_risk_level,
        ),
        [translate("walletManagement.outcome")]: statusText,
        [translate("walletManagement.triggered_by")]: row?.created_by,
        // Details: the raw provider response (JSON stringified) so anyone
        // who wants the full payload can see it without re-querying.
        [translate("walletManagement.details")]: row?.merkle_science_response
          ? JSON.stringify(row.merkle_science_response)
          : "",
      };
    });
    downloadFiles(`Risk Checking Record Report`, res, {});
  };

  // The current filter values feed the request payload. `cacheParams` is the
  // frozen copy actually sent on the last Search click — re-running the
  // effect on raw `fields` would refetch on every keystroke. The pattern
  // matches the sibling `RequestRecordTab`.
  const [fields, setFields] = useState<fieldsFace>(initFields);
  const DateObj = { RescanTime: useDatePicker() };
  // Resolve the user-selected chain name into the (chain_type, chain_id)
  // pair the API expects. The chain name is the dropdown's `choice`, so
  // look it up at request-build time and skip the pair entirely when the
  // user hasn't picked one.
  //
  // Each field is mapped through `nonNullish` so `null` / `undefined`
  // values don't get serialized into the query string as the literal
  // "null" — axios drops undefined params, but it serializes null.
  const selectedChain = fields.chain_name
    ? chainObj[fields.chain_name]
    : undefined;
  const displayNamesArr = separateBatchStrings(fields.displayNamesString);
  // Coerce null/undefined to undefined so axios drops the param entirely
  // instead of serializing the literal "null". Chain fields are typed as
  // `string` on the wire — convert the in-memory `chainObj` numbers via
  // String() so they line up with `chainType: string` in the request type.
  const getStatuses = (outcome: string): number[] | undefined => {
    if (!outcome) return undefined;
    const numeric = Number(outcome);
    if (numeric === WalletRiskLevelRescanStatus.Pending) {
      return [
        WalletRiskLevelRescanStatus.Pending,
        WalletRiskLevelRescanStatus.Expired,
      ];
    }
    return [numeric];
  };
  const params = {
    chainType:
      nonNullish(selectedChain?.chain_type) === undefined
        ? undefined
        : String(selectedChain!.chain_type),
    chainId: nonNullish(selectedChain?.chain_id),
    displayNames: displayNamesArr.length > 0 ? displayNamesArr : undefined,
    walletAddress: fields.wallet_address || undefined,
    statuses: getStatuses(fields.outcome),
    createdBy: fields.triggered_by?.trim() || undefined,
    createdDateFrom: DateObj.RescanTime.start || undefined,
    createdDateTo: DateObj.RescanTime.end || undefined,
    page,
    pageSize: tableConfig.pageSize,
  };
  const [cacheParams, setCacheParams] = useState(params);

  // Initial mount + whenever the user paginates or re-runs Search (which
  // updates cacheParams), refetch. `isSkipErrorHandling` isn't needed here —
  // a global toast on a query failure is the right behavior.
  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      zusDialog.openExtra("loadingDialog");
      const res = await getRiskCheckingRecords({ ...cacheParams, page });
      zusDialog.closeExtra();
      if (cancelled) return;
      if (!res) return;
      setList({ rows: res.rows, count: res.count });
      // The server-supplied Merkle Science timeout (seconds, string) drives
      // the table's lock countdown and the Apply button's disabled state.
      // Set it once per fetch so re-running Search picks up the latest
      // server-side value.
      setMerkleSciApiTimeoutSec(res.merkle_sci_api_timeout_sec);
    }
    fetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, refreshBool, cacheParams]);

  const onSearch = () => {
    setPage(0);
    setCacheParams(params);
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.RescanTime.clearDate();
  };

  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar
          fields={fields}
          setFields={setFields}
          DateObj={DateObj}
          onSearch={onSearch}
          onReset={onReset}
          onExport={onExport}
          hasExportPermission={hasPermission(Pkey.Export)}
        />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <TableTab>
          <RiskCheckingRecordTabList
            list={list}
            page={page}
            setPage={setPage}
            refresh={refresh}
            merkleSciApiTimeoutSec={merkleSciApiTimeoutSec}
          />
        </TableTab>
      </Container>
    </Box>
  );
};

function FilterBar(props: {
  fields: fieldsFace;
  setFields: Dispatch<SetStateAction<fieldsFace>>;
  DateObj: { RescanTime: ReturnType<typeof useDatePicker> };
  onSearch: () => void;
  onReset: () => void;
  onExport: () => void;
  hasExportPermission: boolean;
}) {
  // Filter state is wired up but the API isn't ready yet, so we keep the
  // controls visible (matches the screenshot) without firing requests.
  //
  // Labels follow the "Please {enter|select} {FieldName}" convention from
  // sibling tabs — placeholder text is intentionally omitted so once a
  // filter is active the field reads cleanly with no leftover hint text.
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  // Used by `outcomeOptions` below to translate enum names.
  const tEnum = (key: string) => translate(`enumConstants.${key}`);
  const { fields, setFields, DateObj, onSearch, onReset, onExport } = props;

  // Split the comma-separated display-name input into the array shape the
  // API expects (mirrors `WalletManagementTab`'s `displayNamesString` pattern).
  const displayNamesArr = separateBatchStrings(fields.displayNamesString);

  // Outcome enum is `WalletRiskLevelRescanStatus` (Failed = -1, Pending = 1,
  // Applied = 2, Expired = 3). TypeScript numeric enums emit BOTH a forward
  // ({ "-1": "Failed" }) and reverse ({ "Failed": -1 }) mapping. Object.entries
  // surfaces both, so we filter to the string keys (the names) before sorting.
  const outcomeOptions = (() => {
    const entries = Object.entries(WalletRiskLevelRescanStatus).filter(
      ([key]) => isNaN(Number(key)) && key !== "Expired",
    );
    entries.sort(([, a], [, b]) => String(a).localeCompare(String(b)));
    return entries.map(([name, value]) => ({
      key: value as number,
      // The translation keys live in the project's `enumConstants` namespace
      // under `RescanStatus_*` so they don't collide with the generic
      // `Failed` / `Pending` already used by other enums.
      label: tEnum(`RescanStatus_${name}`),
    }));
  })();
  void EnumRiskLevel; // imported for symmetry with the risk-level filter

  const onChange =
    (type: keyof fieldsFace) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((prev) => ({ ...prev, [type]: value }));
    };

  const F = genField({ t }, [
    [
      "wallet_address",
      <MpTextField
        fullWidth
        label={t("label_wallet_address")}
        value={fields.wallet_address}
        onChange={onChange("wallet_address")}
      />,
    ],
    [
      "display_name",
      <MpTextField
        fullWidth
        label={t("label_display_name")}
        placeholder={t("label_display_name")}
        value={fields.displayNamesString}
        onChange={onChange("displayNamesString")}
        // Match `WalletManagementTab`: comma-separated input, with the live
        // count in the input adornment so the user knows how many names
        // they'll be matching against.
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {displayNamesArr.length}
            </InputAdornment>
          ),
        }}
      />,
    ],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("label_chain_name")}
        choice={fields.chain_name}
        setChoice={(value) =>
          setFields((prev) => ({ ...prev, chain_name: value }))
        }
      />,
    ],
    [
      "outcome",
      <FormControl fullWidth>
        <InputLabel id="risk-outcome-label">{t("label_outcome")}</InputLabel>
        <Select
          labelId="risk-outcome-label"
          label={t("label_outcome")}
          name="outcome"
          value={fields.outcome}
          onChange={(e) =>
            setFields((prev) => ({
              ...prev,
              outcome: e.target.value as string,
            }))
          }
        >
          {outcomeOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>,
    ],
    [
      "triggered_by",
      <MpTextField
        fullWidth
        label={t("label_triggered_by")}
        value={fields.triggered_by}
        onChange={onChange("triggered_by")}
      />,
    ],
    [
      // The DatePicker's default labels ("Start Date" / "End Date") come from
      // the common namespace — no overrides needed. The eyebrow label we
      // used to render above was breaking the layout; passing the label
      // straight to the picker would have duplicated it, so we just let
      // the picker describe itself.
      "rescan_time",
      <DateObj.RescanTime.Picker type="dateTime" />,
    ],
  ]);

  return (
    <>
      <Box sx={customSx.filterCmdBar}>
        {props.hasExportPermission && (
          <GeneralBtn label="export" onClick={props.onExport} />
        )}
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.wallet_address}
        {F.display_name}
        {F.chain_name}
        {F.outcome}
        {F.triggered_by}
        {F.rescan_time}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

export default RiskCheckingRecordTab;
